import { useState } from 'react';
import { BomCRUDSchema } from 'types/bom';
import { useUpdateBomMutation } from 'store/slices/apiV1/bom';
import { getCopy } from 'ui-component/ArchiveDialog/copy';
import { useUpdateProductionRunMutation } from 'store/slices/apiV1/production';
import { ProductionRun } from 'types/production';
import { ManagementActionTypes } from 'types';
import { handleErr } from 'utils/functions';
import { useUpdatePurchaseOrderMutation } from 'store/slices/apiV1/procurement';
import useSnackbar from 'hooks/useSnackbar';
import {
  useArchiveTeamMutation,
  useUnarchiveTeamMutation,
} from 'store/slices/apiV1/properties';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';

interface useHandleArchiveProps {
  id: BomCRUDSchema['id'] | ProductionRun['id'];
  actionType: Exclude<
    keyof typeof ManagementActionTypes,
    'supplier' | 'purchaseRule'
  >;
  archived: boolean;
}

export default function useHandleArchive({
  id,
  actionType,
  archived,
}: useHandleArchiveProps) {
  const copy = getCopy(archived);
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();

  const { dispatchSuccessSnackbar } = useSnackbar();
  const [updateBom, { isLoading: isUpdatingBom, isSuccess: isSuccessBom }] =
    useUpdateBomMutation();
  const [
    updateProductionRun,
    { isLoading: isUpdatingProductionRun, isSuccess: isSuccessProductionRun },
  ] = useUpdateProductionRunMutation();
  const [
    updatePurchaseOrder,
    { isLoading: isUpdatingPurchaseOrder, isSuccess: isSuccessPurchaseOrder },
  ] = useUpdatePurchaseOrderMutation();
  const [
    archiveTeam,
    { isLoading: isArchivingTeam, isSuccess: isSuccessArchivingTeam },
  ] = useArchiveTeamMutation();
  const [
    unarchiveTeam,
    { isLoading: isUnarchivingTeam, isSuccess: isSuccessUnarchivingTeam },
  ] = useUnarchiveTeamMutation();

  const [error, setError] = useState<string | null>(null);

  const handleArchive = async () => {
    setError(null);
    try {
      if (actionType === ManagementActionTypes.bom) {
        await updateBom({
          id,
          payload: { archived: !archived },
        }).unwrap();
      }
      if (actionType === ManagementActionTypes.productionRun) {
        await updateProductionRun({
          id,
          payload: { archived: !archived },
        }).unwrap();
      }
      if (actionType === ManagementActionTypes.purchaseOrder) {
        await updatePurchaseOrder({
          purchaseOrderId: id,
          payload: { archived: !archived },
        }).unwrap();
      }
      if (actionType === ManagementActionTypes.team && !archived) {
        await archiveTeam(id).unwrap();
      }
      if (actionType === ManagementActionTypes.team && archived) {
        await unarchiveTeam(id).unwrap();
      }
      dispatchSuccessSnackbar(
        handleReplaceWithCustomTeamName(copy[actionType].successMessage)
      );
    } catch (err) {
      handleErr(err, setError);
    }
  };

  return {
    handleArchive,
    isLoading:
      isUpdatingBom ||
      isUpdatingProductionRun ||
      isUpdatingPurchaseOrder ||
      isArchivingTeam ||
      isUnarchivingTeam,
    isSuccess:
      isSuccessBom ||
      isSuccessProductionRun ||
      isSuccessPurchaseOrder ||
      isSuccessArchivingTeam ||
      isSuccessUnarchivingTeam,
    error,
    resetError: () => setError(null),
  };
}
